import type { DownloadTicket, Grant } from "./marketplace-client";

/**
 * The browser half of the upload lifecycle: hash, send, and fetch back.
 *
 * The API never sees a file body — it issues a signed, single-purpose grant
 * and the bytes go straight to the store (docs/architecture.md §18). So this
 * module is the only place in the portal that talks to something other than
 * /marketplace/v1, and everything it knows about a stored object is what a
 * grant told it.
 */

/**
 * grantURL is a grant URL as THIS browser should call it.
 *
 * The signature covers the operation, the prefix, the key and the expiry —
 * never the origin — so one grant verifies at whichever host serves it (both
 * platform-api and marketplace-api mount the same handler). In production
 * STORAGE_PUBLIC_BASE_URL names the bucket and the URL is used exactly as
 * issued, because a bucket answers its own CORS preflight.
 *
 * Locally it names a loopback PORT, which is a third origin to the browser
 * and answers no preflight at all — a cross-origin PUT there fails before it
 * is ever sent. The dev server proxies the identical path, so a loopback
 * grant is rewritten onto this origin rather than growing a CORS surface on
 * a service that deliberately has none.
 */
export function grantURL(raw: string): string {
  let parsed: URL;
  try {
    parsed = new URL(raw, window.location.origin);
  } catch {
    return raw;
  }
  if (parsed.origin === window.location.origin || !isLoopback(parsed.hostname)) {
    return parsed.toString();
  }
  return parsed.pathname + parsed.search;
}

function isLoopback(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]";
}

/**
 * sha256Hex is the checksum the upload grant is issued against.
 *
 * Computed over the whole file in memory, which is honest about this phase's
 * ceiling: MARKETPLACE/STORAGE_MAX_OBJECT_BYTES bounds an upload well below
 * what a tab can hold, and a streaming digest would be complexity bought for
 * a size the server refuses anyway. crypto.subtle exists only in a secure
 * context — https, or localhost, which development is.
 */
export async function sha256Hex(file: File): Promise<string> {
  if (!globalThis.crypto?.subtle) {
    throw new Error(
      "This browser will not hash files on an insecure origin. Open the portal over https or on localhost.",
    );
  }
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * putBytes sends the file to the store with the grant it was issued.
 *
 * No credentials: the signature on the URL is the entire authorisation, and
 * attaching the SSO cookie to a bucket request would be handing a session to
 * a hostname whose only job is holding bytes.
 */
export async function putBytes(grant: Grant, file: File): Promise<void> {
  const response = await fetch(grantURL(grant.url), {
    method: grant.method,
    body: file,
    credentials: "omit",
  });
  if (!response.ok) {
    throw new Error(
      response.status === 401
        ? "That upload link has expired. Start the upload again."
        : `The file could not be stored (${response.status}).`,
    );
  }
}

/**
 * startDownload hands the ticket to the browser.
 *
 * An anchor click rather than a navigation: the grant handler answers with
 * Content-Disposition: attachment and a server-generated name, so the page
 * stays where it is and the bytes are never rendered — which is the whole
 * point of refusing to interpret an arbitrary binary.
 */
export function startDownload(ticket: DownloadTicket): void {
  const link = document.createElement("a");
  link.href = grantURL(ticket.url);
  link.download = ticket.filename;
  link.rel = "noreferrer";
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/** Bytes, for a person. */
export function formatBytes(bytes: number | undefined): string {
  if (bytes === undefined) {
    return "—";
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit]}`;
}
